import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { add } from "date-fns";

import {
  FormLabel,
  FormInputHolder,
  FormInput,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormDateInput,
  FormTimeInput,
} from "../form";
import babyIcons from "../icons/babyIcons";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import Temperature from "./FieldExcerpts/Temperature";
import Duration, { addDateAndTimeFields, getDurationStrict } from "./Duration";

import { required, checkTime } from "../../helpers/formValidators";
import { Error } from "../form/BaseForm";

import "./ActivityForm.css";
import { OnChange } from "react-final-form-listeners";
import { FormContext } from "../../context/form-context";
import { FormSpy } from "react-final-form";
import { PrimaryButton, SecondaryButton } from "../buttons";

const ActivityForm = (props) => {
  const {
    babies,
    chosenActivity,
    selectedProfile,
    basicRecordsPost,
    basicRecordsPatch,
    accountId,
    hideTrackerModals,
    handleRecords,
    profileOptions,
    showUnSavedModal,
    showSaveEntryModal,
    showFirstTimeSavingModal
  } = props;

  const defaultValues = {
    babyId: null,
    providerId: selectedProfile.id,
    date: new Date(),
    startTime: new Date(),
    endTime: add(new Date(), { minutes: 10 })
  };

  const [initialValues, setInitialValues] = useState(undefined);

  const formContext = useContext(FormContext);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  useEffect(() => {
    // if there's form data in the context, use that instead of using default values
    if (formContext && Object.keys(formContext.formValues).length > 0) {
      setInitialValues(formContext.formValues);
      return
    }

    const latestRecord = handleRecords('basic', null) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord
    });
  }, []);

  const onSubmit = (values) => {
    const hasDuration = chosenActivity.duration ? true : false;

    const recordBody = {
      ...values,
      activityType: chosenActivity.value,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.startTime),
      duration: hasDuration
        ? getDurationStrict(values.startTime, values.endTime)
        : 0,
      endTime: hasDuration
        ? addDateAndTimeFields(values.date, values.endTime)
        : null
    };

    if (values.id) {
      // Update existing record
      return basicRecordsPatch(
        { accountId: accountId, babyId: values.babyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            hideTrackerModals();
          }
        })
        .catch(() => <ErrorMessage />);
    } else {
      // Create new record
      return basicRecordsPost(
        {
          accountId: accountId,
          babyId: babies.length === 1 ? babies[0].id : values.babyId,
        },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => {
          if (data) {
            hideTrackerModals();
          }
        })
        .catch(() => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({
        onSubmit: onSubmit,
      });

      return showSaveEntryModal();
    }
  }

  if (!initialValues) {
    return <div></div>
  }

  return (
    <BaseForm
      onSubmit={onSave}
      validate={chosenActivity && chosenActivity.duration ? checkTime : null}
      initialValues={initialValues}
    >
      <h2>Track {chosenActivity && chosenActivity.label}</h2>

      {babies && babies.length > 1 && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby, index) => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) {
                return;
              }

              const latestRecord =  handleRecords('basic', value) || {};

              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      <FormLabel for="provider">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={profileOptions}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      {chosenActivity && chosenActivity.value === "other" && (
        <FormInput
          name="otherActivity"
          type="text"
          labelRenderer={() => (
            <FormLabel for="otherActivity">Describe Other Activity</FormLabel>
          )}
        />
      )}

      {chosenActivity && chosenActivity.duration ? (
        <Duration />
      ) : (
        <FormInputHolder>
          <FormDateInput
            name="date"
            labelRenderer={() => (
              <FormLabel for="date">Date</FormLabel>
            )}
            dateFormat="MMM, dd yyyy"
            validate={required}
          />
          <FormTimeInput
            name="startTime"
            labelRenderer={() => <FormLabel>Time</FormLabel>}
            validate={required}
          />
        </FormInputHolder>
      )}

      {chosenActivity && chosenActivity.value === "temperature" && (
        <FormInputHolder>
          <Temperature />
        </FormInputHolder>
      )}

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => {
          return (
            <FormInputHolder>
              <SecondaryButton
                type="button"
                label="Cancel"
                onClick={toggleWarningModal}
              />

              <PrimaryButton type="submit" label="Save" disabled={submitting} />
            </FormInputHolder>
          );
        }}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, dirtyFields: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            let actuallyDirty = false;

            formContext.setValues(values);

            Object.keys(visited).forEach((key) => {
              if (visited[key]) {
                actuallyDirty = true;
              }
            });

            actuallyDirty ? formContext.setDirty() : formContext.notDirty();
          }
        }}
      />
    </BaseForm>
  );
};

ActivityForm.propTypes = {
  showSaveEntryModal: PropTypes.func,
};

export default ActivityForm;



// WEBPACK FOOTER //
// ./src/components/ActivityTracker/ActivityForm.js